import React, { useState } from 'react';
import { Box, Typography, Grid, Paper, TextField, Tooltip, IconButton } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import * as Icons from 'src/presentations/components/atom/icons';
import { ICustomIconProps } from 'src/presentations/components/atom/icons/types';

// Get all icon components from the import
const iconComponents: Record<string, React.FC<ICustomIconProps>> = {};
Object.entries(Icons).forEach(([name, component]) => {
  // Only include components that start with "Icon"
  if (name.startsWith('Icon')) {
    iconComponents[name] = component as React.FC<ICustomIconProps>;
  }
});

const IconGallery: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [iconSize, setIconSize] = useState(24);
  const [iconColor, setIconColor] = useState('#E72547');
  const [copiedIcon, setCopiedIcon] = useState('');

  // Filter icons based on search term
  const filteredIcons = Object.entries(iconComponents).filter(([name]) => name.toLowerCase().includes(searchTerm.toLowerCase()));

  const handleCopyIconName = (iconName: string) => {
    navigator.clipboard.writeText(iconName);
    setCopiedIcon(iconName);
    setTimeout(() => setCopiedIcon(''), 2000);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant='h4' gutterBottom>
        Icon Gallery
      </Typography>
      <Typography variant='body1' paragraph>
        Browse all available icon components in the application. Click on an icon to copy its component name.
      </Typography>

      <Box sx={{ mb: 4, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <TextField
          label='Search icons'
          variant='outlined'
          size='small'
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ minWidth: 200 }}
        />
        <TextField
          label='Size'
          type='number'
          variant='outlined'
          size='small'
          value={iconSize}
          onChange={(e) => setIconSize(Number(e.target.value))}
          InputProps={{ inputProps: { min: 12, max: 64 } }}
          sx={{ width: 100 }}
        />
        <TextField
          label='Color'
          type='color'
          variant='outlined'
          size='small'
          value={iconColor}
          onChange={(e) => setIconColor(e.target.value)}
          sx={{ width: 100 }}
        />
      </Box>

      {filteredIcons.length === 0 ? (
        <Typography variant='body1'>No icons found matching your search.</Typography>
      ) : (
        <Grid container spacing={2}>
          {filteredIcons.map(([name, IconComponent]) => (
            <Grid item xs={6} sm={4} md={3} lg={2} key={name}>
              <Paper
                elevation={2}
                sx={{
                  p: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  height: '100%',
                  transition: 'all 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 4,
                  },
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: 80,
                  }}
                >
                  <IconComponent size={iconSize} color={iconColor} />
                </Box>
                <Box sx={{ mt: 2, width: '100%', textAlign: 'center' }}>
                  <Typography
                    variant='body2'
                    sx={{
                      fontFamily: 'monospace',
                      fontSize: '0.8rem',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {name}
                  </Typography>
                  <Tooltip title={copiedIcon === name ? 'Copied!' : 'Copy component name'}>
                    <IconButton size='small' onClick={() => handleCopyIconName(name)} sx={{ mt: 1 }}>
                      <ContentCopyIcon fontSize='small' />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
};

export default IconGallery;
